/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import type { EcsperError, EcsperErrorResponse } from '@ecsper/types';

import { RETRYABLE_STATUS_CODES } from './constants.js';

/**
 * Optional construction inputs for {@link EcsperApiError}.
 */
export interface EcsperApiErrorInit {
  readonly status: number;
  readonly statusText: string;
  readonly errors: readonly EcsperError[];
  readonly requestId: string;
  readonly responseBody?: string | undefined;
}

/**
 * Thrown when an Ecsper API responds with a non-2xx status code.
 *
 * The `errors` array carries the parsed standard error envelope per
 * CTR-MS365-CONTRACTS §"Error Contract" / ECS-DD-MS365-001 §B4. When the
 * server returned a non-JSON body (for example an HTML proxy error page),
 * `errors` contains a single synthetic `EcsperError` describing the status.
 */
export class EcsperApiError extends Error {
  public override readonly name = 'EcsperApiError';
  public readonly status: number;
  public readonly statusText: string;
  public readonly errors: readonly EcsperError[];
  public readonly requestId: string;
  public readonly responseBody: string | undefined;
  public readonly isRetryable: boolean;

  public constructor(init: EcsperApiErrorInit) {
    const primary = init.errors[0];
    const message =
      primary !== undefined
        ? `${primary.code}: ${primary.detail}`
        : `HTTP ${String(init.status)} ${init.statusText}`;
    super(message);
    this.status = init.status;
    this.statusText = init.statusText;
    this.errors = init.errors;
    this.requestId = init.requestId;
    this.responseBody = init.responseBody;
    this.isRetryable = RETRYABLE_STATUS_CODES.includes(init.status);
  }
}

/**
 * Thrown when a fetch attempt fails before any HTTP response is received
 * (network down, DNS resolution failure, TLS error, CORS rejection).
 */
export class EcsperNetworkError extends Error {
  public override readonly name: string = 'EcsperNetworkError';
  public readonly requestId: string;

  public constructor(message: string, requestId: string, options?: { cause?: unknown }) {
    super(message, options as ErrorOptions | undefined);
    this.requestId = requestId;
  }
}

/**
 * Thrown when a request exceeds its configured timeout (via `AbortController`).
 * Subclass of {@link EcsperNetworkError} so that callers handling network
 * failures generically also catch timeouts.
 */
export class EcsperTimeoutError extends EcsperNetworkError {
  public override readonly name = 'EcsperTimeoutError';
  public readonly timeoutMs: number;

  public constructor(timeoutMs: number, requestId: string, options?: { cause?: unknown }) {
    super(`Request timed out after ${String(timeoutMs)}ms`, requestId, options);
    this.timeoutMs = timeoutMs;
  }
}

/** Type guard for {@link EcsperApiError}. */
export function isEcsperApiError(value: unknown): value is EcsperApiError {
  return value instanceof EcsperApiError;
}

/** Type guard for {@link EcsperNetworkError} (matches {@link EcsperTimeoutError} too). */
export function isEcsperNetworkError(value: unknown): value is EcsperNetworkError {
  return value instanceof EcsperNetworkError;
}

/** Type guard for {@link EcsperTimeoutError}. */
export function isEcsperTimeoutError(value: unknown): value is EcsperTimeoutError {
  return value instanceof EcsperTimeoutError;
}

/**
 * Narrow guard for the standard error envelope shape; used by the response
 * parser before constructing an {@link EcsperApiError}.
 */
export function isEcsperErrorResponse(value: unknown): value is EcsperErrorResponse {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { errors?: unknown };
  if (!Array.isArray(candidate.errors)) return false;
  return candidate.errors.every((e) => {
    if (typeof e !== 'object' || e === null) return false;
    const item = e as { code?: unknown; title?: unknown; detail?: unknown };
    return (
      typeof item.code === 'string' &&
      typeof item.title === 'string' &&
      typeof item.detail === 'string'
    );
  });
}
